(function () {
  class ProductAddToCart extends HTMLElement {
    constructor() {
      super();
      this._variantChangedHandler = this._onVariantChanged.bind(this);
      this._clickHandler = this._onClick.bind(this);
      this._state = {
        productId: null,
        handle: "",
        variantId: null,
        available: true,
        labels: {
          add: "Add to cart",
          soldout: "Sold out",
          comingSoon: "COMING SOON",
          notify: "Notify me",
        },
      };
    }

    connectedCallback() {
      this._hydrateFromDataset();
      this._render();
      window.addEventListener("enroute:variant-changed", this._variantChangedHandler);
      this.addEventListener("click", this._clickHandler);
    }

    disconnectedCallback() {
      window.removeEventListener("enroute:variant-changed", this._variantChangedHandler);
      this.removeEventListener("click", this._clickHandler);
    }

    _hydrateFromDataset() {
      const ds = this.dataset || {};
      const initialAvailable = String(ds.initialVariantAvailable || "").toLowerCase();

      this._state.productId = Number(ds.productId) || null;
      this._state.handle = ds.productHandle || "";
      this._state.title = ds.productTitle || "";
      this._state.variantId = Number(ds.initialVariantId) || null;
      this._state.variant = this._getProductInfo()?.variants?.find((item) => Number(item.id) === this._state.variantId);
      this._state.available = initialAvailable === "false" ? false : true;
      this._state.labels = {
        add: ds.labelAdd || this._state.labels.add,
        comingSoon: ds.labelComingSoon || this._state.labels.comingSoon,
        soldout: ds.labelSoldout || this._state.labels.soldout,
        notify: ds.labelNotify || this._state.labels.notify,
      };
    }

    _onVariantChanged(event) {
      const variant = event?.detail?.variant;
      if (!variant) return;

      this._state.variantId = Number(variant.id) || this._state.variantId;
      this._state.available = !!variant.available;
      this._state.variant = variant;
      this._render();
    }

    _onClick(event) {
      const notifyButton = event.target.closest(".back-in-stock-btn");
      if (notifyButton) {
        event.preventDefault();
        if (typeof BackInStockModal !== "undefined") {
          new BackInStockModal(this._state.variant, this, this._getProductInfo());
        }
        return;
      }

      const addButton = event.target.closest("[data-action='add-to-cart']");
      if (!addButton) return;

      event.preventDefault();
      if (!this._state.variantId) return;

      const productInfo = this._getProductInfo();
      const variant =
        this._state.variant ||
        productInfo?.variants?.find((item) => Number(item.id) === this._state.variantId);
      const variantText =
        variant?.title && variant.title.toLowerCase() !== "default title"
          ? variant.title
          : (variant?.options || [])
              .filter((option) => option && option.toLowerCase() !== "default title")
              .join(" / ");
      const routeRoot = window.Shopify?.routes?.root || "/";

      const payload = {
        items: [
          {
            quantity: 1,
            id: this._state.variantId,
            properties: {},
            productId: this._state.productId,
            handle: this._state.handle,
            title: this._state.title || productInfo?.title || "",
            image:
              variant?.image ||
              productInfo?.media?.[0]?.preview_image ||
              productInfo?.media?.[0]?.src ||
              "",
            variantText,
            url: this._state.handle
              ? `${routeRoot}products/${this._state.handle}`
              : "",
          },
        ],
      };

      if (!this._validateCharmCount()) return;

      const finalPayload = this._processCustomOptions(payload);

      // Merge the letter-customizer's text/style as line item properties.
      // Aborts the add (and surfaces the field error) when its input is invalid.
      if (!this._applyLetterCustomizerProperties(finalPayload)) return;

      // Same contract for the gift-card recipient form (gift card products only).
      if (!this._applyGiftCardRecipientProperties(finalPayload)) return;

      if (variant?.preOrder && variant.preOrderShipDate && finalPayload.items?.[0]?.properties) {
        const shippedByLabel = window._erj_?.getGlobalLocaleStrings?.()?.cart?.shipped_by || "Shipped by";
        finalPayload.items[0].properties[shippedByLabel] = variant.preOrderShipDate;
        if (variant.preOrderShipDateYmd) finalPayload.items[0].properties["_pre_order"] = variant.preOrderShipDateYmd;
      }

      window.enrouteCart?.addToCart?.(finalPayload);
    }

    _getProductInfo() {
      const productCard = this.closest(".product.wp.new-look");
      return window._erj_?.getProductInfo?.(productCard) || {};
    }

    _processCustomOptions(payload) {
      const properties = {};
      const productDetails = this.closest(".product-details.new-look");
      const customOptionGroupContainer = productDetails ? productDetails.querySelector("[data-custom-option-groups]") : null;

      if (!productDetails || !customOptionGroupContainer) {
        if (payload.items && payload.items.length > 0) {
          payload.items[0].properties = properties;
        }
        return payload;
      }

      // Merge bundle items into payload if they exist
      const bundleItemsAttr = customOptionGroupContainer.getAttribute("data-bundle-items");
      if (bundleItemsAttr) {
        try {
          const bundleItems = JSON.parse(bundleItemsAttr);

          // Generate a more robust random identifier
          const bundleId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

          if (payload.items && payload.items.length > 0) {
            const parentProperties = {
              _bundle_id: bundleId,
              _is_bundle_parent: "true",
              _bundle_info: JSON.stringify(bundleItems),
            };

            if (Array.isArray(bundleItems) && bundleItems.length > 0) {
              const bundleNames = bundleItems
                .filter((item) => item.name)
                .map((item) => `${item.name} * ${item.quantity}`)
                .join(", ");
              if (bundleNames) {
                const groupElement = customOptionGroupContainer.querySelector(".custom-option-group[data-group-name]");
                const groupName = groupElement ? groupElement.getAttribute("data-group-name") : "Options";
                parentProperties[groupName] = bundleNames;
              }
            }

            payload.items[0].properties = parentProperties;
          }

          if (Array.isArray(bundleItems)) {
            bundleItems.forEach((item) => {
              const count = item.quantity > 0 ? item.quantity : 1;
              for (let i = 0; i < count; i++) {
                payload.items.push({
                  id: item.id,
                  quantity: 1,
                  properties: {
                    _bundle_id: bundleId,
                  },
                });
              }
            });
          }
        } catch (e) {
          console.error("Failed to parse bundle items:", e);
        }
      }

      return payload;
    }

    /**
     * Reads the <letter-customizer> (if present) and writes its typed text and
     * chosen style onto the first line item's properties so they reach cart/add.js.
     * Returns false when the customizer exists but its input is invalid, after
     * surfacing the field error — the caller should abort the add in that case.
     */
    _applyLetterCustomizerProperties(payload) {
      const productDetails = this.closest(".product-details.new-look");
      const customizer = (productDetails || document).querySelector("letter-customizer");
      if (!customizer || typeof customizer.getCartProperties !== "function") return true;

      const { valid, properties } = customizer.getCartProperties();
      if (!valid) {
        customizer.reportInvalid?.();
        return false;
      }

      const target = payload.items?.[0];
      if (target && properties) {
        target.properties = Object.assign({}, target.properties, properties);
      }
      return true;
    }

    /**
     * Reads the <gift-card-recipient> form (gift card products only) and writes the
     * recipient email/name/send-on/message onto the first line item's properties, so
     * Shopify delivers the gift card to them. Returns false when the buyer opted in
     * but the email is invalid, after surfacing the field error — the caller should
     * abort the add in that case.
     */
    _applyGiftCardRecipientProperties(payload) {
      const productDetails = this.closest(".product-details.new-look");
      const form = (productDetails || document).querySelector("gift-card-recipient");
      if (!form || typeof form.getCartProperties !== "function") return true;

      const { valid, properties } = form.getCartProperties();
      if (!valid) {
        form.reportInvalid?.();
        return false;
      }

      const target = payload.items?.[0];
      if (target && properties) {
        target.properties = Object.assign({}, target.properties, properties);
      }
      return true;
    }

    _validateCharmCount() {
      const productDetails = this.closest(".product-details.new-look");
      const container = productDetails
        ? productDetails.querySelector("[data-custom-option-groups]")
        : document.querySelector("[data-custom-option-groups]");
      if (!container) return true;

      const maxAvailable = parseInt(container.dataset.maxAvailableCount || "9999", 10);
      if (!isFinite(maxAvailable)) return true;

      const bundleItemsAttr = container.getAttribute("data-bundle-items");
      if (!bundleItemsAttr) return true;

      try {
        const bundleItems = JSON.parse(bundleItemsAttr);
        const totalCharmCount = bundleItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        if (totalCharmCount > maxAvailable) {
          const msg = `\n\nOnly ${maxAvailable} item(s) available for the selected variant`;
          document.getElementById("mobileconfirmationtoast")?.swapText(msg);
          document.querySelector("add-success-toast")?.show(msg, 3000);
          return false;
        }
      } catch (_) {}

      return true;
    }

    _render() {
      const showAdd = this._state.available;
      const statusLabel = window._erj_?.isVariantComingSoon(this._state.variant) ? this._state.labels.comingSoon : this._state.labels.soldout;
      const soldOutLabel = `${statusLabel}\u00a0-\u00a0${this._state.labels.notify}`;

      this.innerHTML = `
        <a
          class="${this.closest(".product-details-new-2610") ? "pd2610-btn" : "btn"} red-hvg-add-button new-look"
          data-action="add-to-cart"
          style="display:${showAdd ? "flex" : "none"}"
        >
          <div class="red-hvg-add-button-text">
            <span>${this._state.labels.add}</span>
          </div>
        </a>
        <a
          class="${this.closest(".product-details-new-2610") ? "pd2610-btn" : "btn"} back-in-stock-btn"
          style="display:${showAdd ? "none" : "flex"};justify-content: center;"
        >
          ${soldOutLabel}
        </a>
      `;
    }
  }

  if (!window.customElements.get("product-add-to-cart")) {
    window.customElements.define("product-add-to-cart", ProductAddToCart);
  }
})();
